import { useEffect, useRef, useState } from "react";
import { opsOrbAnalyserStore } from "./ops-orb-analyser-store.js";
import { nextOpsOrbCanvasLease, type OpsOrbCanvasLease } from "./ops-orb-canvas-lease.js";
import { installOpsOrbChunkRecovery } from "./ops-orb-chunk-recovery.js";
import { selectOpsOrbOutputAnalyser } from "./ops-orb-output-authority.js";
import { createOpsSeenStageParticleOrb } from "./ops-seenstage-orb-adapter.js";
import type { OpsOrbVisualState, OpsParticleOrbEngine } from "./ops-orb-engine.js";
import type { OpsVoicePhase } from "./use-ops-voice.js";

type RendererState = "loading" | "three-webgl" | "unavailable";
type OpsOrbProofCanvas = HTMLCanvasElement & {
  __opsOrbCaptureFrameDataUrlForProof?: () => string;
};

export function OpsVoiceOrb({
  phase
}: {
  phase: OpsVoicePhase;
  pulse: number;
  pulseIntensity: number;
}) {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const canvasRef = useRef<OpsOrbProofCanvas | null>(null);
  const engineRef = useRef<OpsParticleOrbEngine | null>(null);
  const stateRef = useRef<OpsOrbVisualState>(visualStateForPhase(phase));
  const recoveryAttemptsRef = useRef(0);
  const foregroundRecoveryNeededRef = useRef(false);
  const [renderer, setRenderer] = useState<RendererState>("loading");
  const [canvasLease, setCanvasLease] = useState<OpsOrbCanvasLease>({ epoch: 0 });

  useEffect(() => {
    const pauseForeground = () => engineRef.current?.setPaused(true);
    const resumeForeground = () => {
      if (document.hidden) return;
      recoveryAttemptsRef.current = 0;
      if (foregroundRecoveryNeededRef.current) {
        foregroundRecoveryNeededRef.current = false;
        setCanvasLease((previous) => nextOpsOrbCanvasLease(previous, "replace"));
        return;
      }
      // `setPaused(false)` deliberately rearms the same RAF lease even if no
      // hidden visibility event reached this retained Edge page.
      engineRef.current?.setPaused(false);
      engineRef.current?.resize();
    };
    const syncVisibility = () => {
      if (document.hidden) pauseForeground();
      else resumeForeground();
    };
    document.addEventListener("visibilitychange", syncVisibility);
    window.addEventListener("pagehide", pauseForeground);
    window.addEventListener("pageshow", resumeForeground);
    syncVisibility();
    return () => {
      document.removeEventListener("visibilitychange", syncVisibility);
      window.removeEventListener("pagehide", pauseForeground);
      window.removeEventListener("pageshow", resumeForeground);
    };
  }, []);

  useEffect(() => installOpsOrbChunkRecovery(), []);

  useEffect(() => {
    setRenderer("loading");
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    let recoveryRequested = false;
    let ownedEngine: OpsParticleOrbEngine | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let unsubscribeAnalyser: (() => void) | null = null;
    let lastRootVisualAt = 0;
    let lastRootSignal = "idle";
    let lastRootAudiblePcm = false;
    const initialization = new AbortController();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const replaceCanvas = (reason: "fatal") => {
      if (cancelled || recoveryRequested) return;
      if (document.hidden) {
        foregroundRecoveryNeededRef.current = true;
        setRenderer("unavailable");
        return;
      }
      if (recoveryAttemptsRef.current >= 2) {
        foregroundRecoveryNeededRef.current = true;
        setRenderer("unavailable");
        return;
      }
      recoveryAttemptsRef.current += 1;
      recoveryRequested = true;
      canvas.dataset.opsOrbRecoveryReason = reason;
      setRenderer("loading");
      setCanvasLease((previous) => nextOpsOrbCanvasLease(previous, "replace"));
    };

    // WHY [BOL-ORB-ENTRY-OWNER-016]: a frozen Samsung tab kept c309's entry
    // module across later releases, then its on-demand Orb request received the
    // SPA HTML fallback because that hashed chunk had been removed. The module
    // loader correctly rejected the non-JavaScript MIME body and left only the
    // unavailable ring. The Orb is primary UI, so the current entry owns its
    // one canonical renderer statically; retained immutable legacy chunks cover
    // already-running tabs. Falsifier: a new build emits an Orb dynamic entry or
    // any foregrounded authenticated tab can again reach the empty ring.
    void createOpsSeenStageParticleOrb(canvas, {
      reducedMotion: reducedMotion.matches,
      onFrame: (frame) => {
        if (cancelled || recoveryRequested) return;
        recoveryAttemptsRef.current = 0;
        const root = rootRef.current;
        if (!root) return;
        if (frame.signal !== lastRootSignal) {
          lastRootSignal = frame.signal;
          root.dataset.signal = frame.signal;
        }
        if (frame.audiblePcm !== lastRootAudiblePcm) {
          lastRootAudiblePcm = frame.audiblePcm;
          root.dataset.audiblePcm = String(frame.audiblePcm);
        }
        const now = performance.now();
        if (now - lastRootVisualAt < 32) return;
        lastRootVisualAt = now;
        // WHY [BOL-ORB-ONE-MOTION-OWNER-003]: the accepted desktop particle
        // radius already contains the calm breath and real PCM envelope. The
        // small supporting core/halo must follow that exact frame, never a CSS
        // keyframe, transcript pulse, phase timer, or second analyser. Falsifier:
        // the light moves while the WebGL body is still, or reduced motion dies.
        // WHY [BOL-ORB-CONTAINED-LIGHT-002]: WebGL particles are the avatar.
        // Supporting light follows their calibrated radius but remains much
        // tighter and dimmer, so white-page contrast comes from discrete red
        // points—not the rejected always-on red haze. Falsifier: core/halo fill
        // the 88px stage or stay constant while an Aoede crest expands the body.
        const engineScale = Math.max(0.82, Math.min(1.75, frame.radius));
        const coreOpacity = Math.max(0.045, Math.min(0.24, 0.045 + frame.glow * 0.15 + frame.level * 0.035));
        const haloOpacity = Math.max(0.012, Math.min(0.12, 0.012 + frame.glow * 0.085 + frame.level * 0.02));
        root.dataset.level = frame.level.toFixed(3);
        root.dataset.bass = frame.bass.toFixed(3);
        root.dataset.mid = frame.mid.toFixed(3);
        root.dataset.treble = frame.treble.toFixed(3);
        root.dataset.radius = frame.radius.toFixed(3);
        root.dataset.glow = frame.glow.toFixed(3);
        root.dataset.engineScale = engineScale.toFixed(3);
        root.style.setProperty("--ops-orb-live-glow", frame.glow.toFixed(3));
        root.style.setProperty("--ops-orb-live-glow-opacity", (0.22 + frame.glow * 0.58).toFixed(3));
        root.style.setProperty("--ops-orb-engine-scale", engineScale.toFixed(3));
        root.style.setProperty("--ops-orb-core-opacity", coreOpacity.toFixed(3));
        root.style.setProperty("--ops-orb-halo-opacity", haloOpacity.toFixed(3));
      },
      onFatalError: () => replaceCanvas("fatal"),
      signal: initialization.signal
    }).then((engine) => {
      if (cancelled || recoveryRequested) {
        engine.destroy();
        return;
      }
      ownedEngine = engine;
      engineRef.current = engine;
      foregroundRecoveryNeededRef.current = false;
      engine.setPaused(document.hidden);
      // WHY [BOL-ORB-PROOF-HOOK-001]: normal playback keeps the fast discarded
      // WebGL backbuffer. Browser proof may request one donor-owned offscreen
      // frame so a transparent/blank canvas can reject release literally.
      if (engine.captureFrameDataUrlForProof) {
        canvas.__opsOrbCaptureFrameDataUrlForProof = () => engine.captureFrameDataUrlForProof!();
      }
      engine.setState(stateRef.current);
      const initialAnalyser = opsOrbAnalyserStore.getSnapshot();
      engine.setAnalyser(selectOpsOrbOutputAnalyser(initialAnalyser), "output");
      unsubscribeAnalyser = opsOrbAnalyserStore.subscribe((snapshot) => {
        engine.setAnalyser(selectOpsOrbOutputAnalyser(snapshot), "output");
      });
      resizeObserver = new ResizeObserver(() => engine.resize());
      resizeObserver.observe(canvas);
      setRenderer("three-webgl");
    }).catch(() => {
      if (!cancelled && !recoveryRequested) setRenderer("unavailable");
    });

    const onReducedMotion = (event: MediaQueryListEvent) => {
      engineRef.current?.setReducedMotion(event.matches);
    };
    const onContextLost = (event: Event) => {
      event.preventDefault();
      // WHY [BOL-ORB-NATIVE-RESTORE-017]: c309 was the last literal Samsung
      // frame with particles. c311 immediately created another context here;
      // Chromium may evict the oldest context while activating that replacement,
      // causing a loss/allocation loop under restored-tab pressure. Three owns
      // native restoration and rebuilds its resources on this same canvas.
      // Visibility only pauses this owner; confirmed fatal initialization alone
      // gets a fresh canvas through the bounded lease.
      // Falsifier: a native loss changes the canvas epoch, two renderers overlap,
      // or webglcontextrestored fails to resume this exact visible owner.
      setRenderer("loading");
    };
    const onContextRestored = () => {
      // Three registered first and synchronously rebuilt its GL state. Promote
      // only after every listener for this restoration event has completed.
      queueMicrotask(() => {
        if (
          cancelled ||
          recoveryRequested ||
          engineRef.current !== ownedEngine
        ) return;
        ownedEngine?.resize();
        ownedEngine?.setPaused(document.hidden);
        setRenderer("three-webgl");
      });
    };
    reducedMotion.addEventListener("change", onReducedMotion);
    canvas.addEventListener("webglcontextlost", onContextLost);
    canvas.addEventListener("webglcontextrestored", onContextRestored);

    return () => {
      cancelled = true;
      initialization.abort();
      reducedMotion.removeEventListener("change", onReducedMotion);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      canvas.removeEventListener("webglcontextrestored", onContextRestored);
      resizeObserver?.disconnect();
      unsubscribeAnalyser?.();
      delete canvas.__opsOrbCaptureFrameDataUrlForProof;
      if (engineRef.current === ownedEngine) engineRef.current = null;
      ownedEngine?.destroy();
    };
  }, [canvasLease.epoch]);

  useEffect(() => {
    const nextState = visualStateForPhase(phase);
    stateRef.current = nextState;
    engineRef.current?.setState(nextState);
  }, [phase]);

  return (
    <span
      ref={rootRef}
      className={`ops-voice-orb ops-voice-orb--${phase}`}
      role="img"
      aria-label={`Luna ${phaseLabel(phase)}`}
      data-renderer={renderer}
      data-phase={phase}
      data-signal="idle"
      data-audible-pcm="false"
      data-accent="red"
      data-radius="1.000"
      data-glow="0.000"
    >
      <span className="ops-voice-orb__core" aria-hidden="true" />
      <canvas key={canvasLease.epoch} ref={canvasRef} className="ops-voice-orb__canvas" aria-hidden="true" />
      <span className="ops-voice-orb__glow" aria-hidden="true" />
    </span>
  );
}

export function visualStateForPhase(phase: OpsVoicePhase): OpsOrbVisualState {
  if (phase === "speaking") return "speaking";
  if (phase === "thinking") return "thinking";
  if (["voice_arming", "listening", "interrupting"].includes(phase)) return "listening";
  return "idle";
}

function phaseLabel(phase: OpsVoicePhase): string {
  return ({
    text_idle: "ready for text",
    voice_arming: "preparing to listen",
    listening: "listening",
    thinking: "checking with Luna",
    speaking: "speaking",
    interrupting: "stopping speech",
    error: "voice unavailable"
  } satisfies Record<OpsVoicePhase, string>)[phase];
}
